import * as React from 'react'
import { ToastMessage } from '@ds/ui'
import { Message } from './types'
export type ShouldRemovePredicate = (message: Message) => boolean

interface MessageWithTimer extends Message {
  timer: number
}

export interface AppMessengerContextValue {
  submitMessages: (messages: Message[], allowDuplicates?: boolean) => void
  removeAllMessages: () => void
  removeMessages: (shouldRemove: ShouldRemovePredicate) => void
}

const uninitializedContextValue: AppMessengerContextValue = {
  submitMessages: (_messages: Message[], _allowDuplicates?: boolean) => {
    throw new Error('AppMessengerContext not initialized')
  },
  removeAllMessages: () => {
    throw new Error('AppMessengerContext not initialized')
  },
  removeMessages: (_removeFunction: ShouldRemovePredicate) => {
    throw new Error('AppMessengerContext not initialized')
  },
}

export const AppMessengerContext: React.Context<AppMessengerContextValue> =
  React.createContext(uninitializedContextValue)

export const AppMessageSubmitter = AppMessengerContext.Consumer

export const useAppMessenger = (): AppMessengerContextValue => {
  return React.useContext(AppMessengerContext)
}

export const AppMessenger: React.FC = (props) => {
  const [messages, setMessages] = React.useState<Message[]>([])

  const messengerContext = React.useMemo(() => {
    function isDuplicate(message: Message, existingMessages: Message[]) {
      if (!message.id) {
        return false
      }
      return (
        existingMessages.find(
          (existingMessage) => message.id === existingMessage.id
        ) !== undefined
      )
    }

    function uniqueMessages(
      newMessages: Message[],
      existingMessages: Message[]
    ) {
      return newMessages.filter(
        (message) => !isDuplicate(message, existingMessages)
      )
    }

    return {
      submitMessages: (newMessages: Message[], allowDuplicates = false) =>
        setMessages((prevMessages) => {
          const messagesToAdd = allowDuplicates
            ? newMessages
            : uniqueMessages(newMessages, prevMessages)
          if (messagesToAdd.length) {
            return [...prevMessages, ...messagesToAdd]
          }
          return prevMessages
        }),
      removeAllMessages: () => setMessages([]),
      removeMessages: (shouldRemove: ShouldRemovePredicate) => {
        setMessages((prevMessages) => {
          const messagesToKeep = prevMessages.filter(
            (message) => !shouldRemove(message)
          )
          return messagesToKeep
        })
      },
    }
  }, [])

  function handleRemove(message: Message) {
    setMessages((prevMessages) => {
      const newMessages = prevMessages.filter((msg) => msg !== message)
      return newMessages
    })
  }

  return (
    <AppMessengerContext.Provider value={messengerContext}>
      {props.children}
      <AppMessagesViewer
        messages={messages}
        onRemove={(message) => handleRemove(message)}
      />
    </AppMessengerContext.Provider>
  )
}

interface Props {
  messages: Message[]
  onRemove: (message: Message) => void
}

class AppMessagesViewer extends React.Component<Props> {
  render() {
    const keysUsed = new Set<string>()
    return (
      <ToastMessage.Portal>
        {this.props.messages.map((message) => {
          const key = this.createUniqueKey(message, keysUsed)
          keysUsed.add(key)
          return (
            <ToastMessage
              key={key}
              visible={true}
              kind={message.kind}
              closeButton={this.renderMessageCloseButton(message)}
              data-qa={message['data-qa']}
              visuallyHidden={!!message.visuallyHidden}
              action={this.renderMessageAction(message)}
            >
              {message.content}
            </ToastMessage>
          )
        }, this)}
      </ToastMessage.Portal>
    )
  }

  private createUniqueKey(message: Message, keysUsed: Set<string>) {
    // just an optimization for react...does not need to be consistent across renderings
    let key =
      message.id || (typeof message.content === 'string' ? message.content : '')
    while (keysUsed.has(key)) {
      key += '.1'
    }
    return key
  }

  private renderMessageCloseButton(message: Message) {
    return (
      <ToastMessage.Close
        onClick={() => {
          if (message.onClose) {
            message.onClose(message)
          }
          this.props.onRemove(message)
        }}
        data-qa={message['data-qa'] + ':close'}
      />
    )
  }

  private renderMessageAction(message: Message) {
    const action = message.action
    if (!action) return undefined
    return (
      <ToastMessage.Action
        onClick={(e) => {
          if (action.onClick) {
            action.onClick(e)
          }
          this.props.onRemove(message)
        }}
        data-qa={message['data-qa'] + ':action'}
        text={action.text}
        href={action.href}
        target={action.target}
      />
    )
  }

  private initializeMessageDismiss(message: Message) {
    const messageWithTimer = message as MessageWithTimer
    if (message.maxShowMillis && !messageWithTimer.timer) {
      messageWithTimer.timer = window.setTimeout(
        () => this.props.onRemove(message),
        message.maxShowMillis
      )
    }
  }

  componentDidMount() {
    this.props.messages.forEach((message) =>
      this.initializeMessageDismiss(message)
    )
  }

  componentDidUpdate() {
    this.props.messages.forEach((message) =>
      this.initializeMessageDismiss(message)
    )
  }

  componentWillUnmount() {
    ;(this.props.messages as MessageWithTimer[]).forEach((message) => {
      if (message.timer) {
        window.clearTimeout(message.timer)
      }
    })
  }
}
